/**
 * Insights article contents rail.
 *
 * Two jobs:
 *   1. Collapse the rail on narrow viewports. The markup ships open so that
 *      readers without JavaScript still get the full list.
 *   2. Mark the section currently being read, using IntersectionObserver
 *      against the headings the rail links to.
 */

(function (Drupal, once) {
  "use strict";

  const DESKTOP = window.matchMedia("(min-width: 64rem)");

  /**
   * Keeps the disclosure closed on narrow viewports and open on wide ones,
   * unless the reader has toggled it themselves during this visit.
   *
   * @param {HTMLDetailsElement} details
   */
  function syncDisclosure(details) {
    let touched = false;

    details.addEventListener("toggle", () => {
      if (!DESKTOP.matches) {
        touched = true;
      }
    });

    const apply = () => {
      if (DESKTOP.matches) {
        // Desktop hides the summary entirely, so a closed rail would be
        // unreachable.
        details.open = true;
        touched = false;
      } else if (!touched) {
        details.open = false;
      }
    };

    apply();
    DESKTOP.addEventListener("change", apply);
  }

  /**
   * Highlights the rail entry for the heading nearest the top of the viewport.
   *
   * @param {HTMLElement} toc
   */
  function trackReadingPosition(toc) {
    const links = Array.from(toc.querySelectorAll("[data-k-toc-link]"));
    if (!links.length || !("IntersectionObserver" in window)) {
      return;
    }

    const byId = new Map();
    const targets = [];

    links.forEach((link) => {
      const target = document.getElementById(link.dataset.kTocLink);
      if (target) {
        byId.set(target, link);
        targets.push(target);
      }
    });

    if (!targets.length) {
      return;
    }

    const visible = new Set();

    const mark = () => {
      // Document order, so the topmost visible section wins when several
      // are on screen at once.
      const current = targets.find((target) => visible.has(target));

      links.forEach((link) => {
        const active = current && byId.get(current) === link;
        if (active) {
          link.setAttribute("aria-current", "true");
        } else {
          link.removeAttribute("aria-current");
        }
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.add(entry.target);
          } else {
            visible.delete(entry.target);
          }
        });
        mark();
      },
      {
        // Bottom margin keeps the last short section from never activating.
        rootMargin: "-20% 0px -65% 0px",
        threshold: 0,
      },
    );

    targets.forEach((target) => observer.observe(target));
  }

  Drupal.behaviors.keidoArticleToc = {
    attach(context) {
      once("keidoArticleToc", "[data-k-toc]", context).forEach((toc) => {
        syncDisclosure(toc);
        trackReadingPosition(toc);
      });
    },
  };
})(Drupal, once);
